/** The "Start a conversation" card.
 *
 * Shared by the agent-level conversations tab and a project's own, which had
 * drifted apart in every way two copies of a form can: one gated its submit on
 * an empty message and one did not, one offered a "Talk to" selector and one
 * assumed the Manager, and the two laid their controls out differently. Every
 * one of those was fixed on one page first and the other later.
 *
 * It owns the composer's state and reports a finished choice; the caller owns
 * the mutation and where to navigate afterwards, because those are the two
 * things that genuinely differ (`/stateful-runs/start` on a project,
 * `/stateful-runs/start-agent-chat` across the portfolio).
 */
import { useRef, useState } from "react";

import { useAttachments } from "../api/hooks/useAttachments";
import type { HeldAttachments } from "../api/hooks/useAttachments";
import type { AgentPromptSummary } from "../api/hooks/useStatefulAgents";
import { sendOnEnter } from "../utils/sendShortcut";
import { AttachButton, AttachedFiles } from "./AttachmentsBar";
import { ProjectPicker } from "./ProjectPicker";
import { SpecialistPicker } from "./SpecialistPicker";
import controls from "./FormControls.module.css";
import styles from "./ConversationStarter.module.css";

export interface ConversationChoice {
  content: string;
  /** "" when the Manager fronts the suite, which is the default. */
  agent: string;
  /** null = the whole team. Always null when a specialist answers: it delegates
   * to nobody, so a subset would be meaningless. */
  specialists: string[] | null;
  /** Empty = every project. Only ever populated when `withSiteScope` is on. */
  scopeProjectIds: string[];
  isPrivate: boolean;
  /** Uploaded to the holding area; sent with the opening message. */
  attachments: HeldAttachments;
}

export const ConversationStarter = ({
  roster,
  placeholder,
  withSiteScope = false,
  isLoading,
  error,
  onStart,
  initialMessage = "",
  initialAgent = "",
}: {
  /** Every project agent, Manager included. */
  roster: readonly AgentPromptSummary[];
  placeholder: string;
  /** Offer the "About <sites>" picker. A project's own tab is already one site. */
  withSiteScope?: boolean;
  isLoading: boolean;
  /** Rendered under the card. Owned by the caller, which makes the request. */
  error: string | null;
  /** ``started`` is called once the conversation exists: only then is the box cleared,
   * so a refused start keeps the message and its attachments. */
  onStart: (choice: ConversationChoice, started: () => void) => void;
  /** Prefills the box from a link that asks the agents something, never sent unseen. */
  initialMessage?: string;
  /** The agent that link addresses; "" for the Manager. */
  initialAgent?: string;
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [message, setMessage] = useState(initialMessage);
  const [talkTo, setTalkTo] = useState(initialAgent);
  const [specialists, setSpecialists] = useState<string[] | null>(null);
  const [scopeProjectIds, setScopeProjectIds] = useState<string[]>([]);
  const [isPrivate, setIsPrivate] = useState(false);
  const attachments = useAttachments();
  // Only the Manager delegates, so the subset has no meaning when a specialist
  // is answering about its own work - and showing it would imply otherwise.
  // A prefilled agent the roster does not list answers as the Manager, as the select shows.
  const answering = roster.some((a) => !a.is_manager && a.agent === talkTo) ? talkTo : "";
  const managerAnswers = !answering;

  const submit = () => {
    const content = message.trim();
    // Repeated rather than left to `disabled`: the keyboard never consults a
    // disabled button.
    if (!content || isLoading || attachments.uploading || attachments.failed) return;
    const sent = attachments.ready;
    onStart(
      {
        content,
      agent: answering,
      specialists: managerAnswers ? specialists : null,
      scopeProjectIds: withSiteScope ? scopeProjectIds : [],
        isPrivate,
        attachments: sent,
      },
      () => {
        setMessage("");
        attachments.release(sent);
      },
    );
  };

  return (
    <section className={styles.card}>
      <h2>Start a conversation</h2>
      <form
        ref={formRef}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <textarea
          className={styles.composer}
          rows={3}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          onKeyDown={sendOnEnter(() => formRef.current?.requestSubmit())}
          onPaste={attachments.onPaste}
          placeholder={placeholder}
        />
        {(attachments.items.length > 0 || attachments.rejection) && (
          <div className={styles.row}>
            <AttachedFiles attachments={attachments} disabled={isLoading} />
          </div>
        )}
        {/* Who answers, then what the conversation is about, then who they can
            bring in - the order the choices actually depend on each other. */}
        <div className={styles.row}>
          {roster.length > 1 && (
            <label className={controls.control}>
              Talk to
              <select
                className={controls.select}
                value={answering}
                onChange={(event) => setTalkTo(event.target.value)}
              >
                {roster.map((a) => (
                  <option key={a.agent} value={a.is_manager ? "" : a.agent}>
                    {a.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          {withSiteScope && (
            // Nothing picked reaches every project, which is what an open-ended portfolio question needs.
            <span className={controls.control}>
              About
              <ProjectPicker
                projectIds={scopeProjectIds}
                placeholder="any site"
                onChange={setScopeProjectIds}
              />
            </span>
          )}
        </div>
        {managerAnswers && (
          <div className={styles.row}>
            <SpecialistPicker
              roster={roster}
              chosen={specialists}
              onChange={setSpecialists}
            />
          </div>
        )}
        <div className={styles.row}>
          <AttachButton attachments={attachments} disabled={isLoading} />
          <button
            className={styles.primary}
            type="submit"
            disabled={
              !message.trim() || isLoading || attachments.uploading || attachments.failed
            }
          >
            {isLoading
              ? "Starting…"
              : attachments.uploading
                ? "Uploading…"
                : "Start a conversation"}
          </button>
          {/* Off by default: these are a shared team surface, so visible is the
              norm and hiding one is the deliberate act. */}
          <label className={styles.privateChoice}>
            <input
              type="checkbox"
              checked={isPrivate}
              onChange={(event) => setIsPrivate(event.target.checked)}
            />
            Private - only I can see the transcript
          </label>
        </div>
      </form>
      {error && <p className={styles.error}>{error}</p>}
    </section>
  );
};
